/** El mismo bucle que abre `itsthebest1111.com`, ya copiado al repositorio. */
const VIDEO_SRC = "/assets/video/hero-loop.mp4";

/**
 * El primer fotograma del bucle, extraído con ffmpeg. Es el póster del
 * vídeo Y la imagen fija que ve quien pide movimiento reducido: al ser el
 * fotograma 0, cuando el vídeo sí arranca no hay salto visible entre lo
 * que ya estaba pintado y lo que empieza a reproducirse.
 */
const POSTER_SRC = "/assets/video/hero-loop-poster.webp";

export interface ShopHeroVideoProps {
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
}

/**
 * El fondo de la portada: el bucle de la página principal.
 *
 * ── Con movimiento reducido no hay vídeo, hay imagen ─────────────────
 * Y no un `<video>` en pausa: un `<img>`. La diferencia importa por lo
 * que NO ocurre — el navegador no descarga el 1.2 MB del MP4, no
 * descodifica nada y no queda un elemento multimedia al que un
 * `autoplay` del navegador o un gesto accidental puedan darle al play.
 * Quien pidió que nada se moviera se ahorra además la descarga.
 *
 * ── Los cuatro atributos del vídeo, y por qué cada uno ───────────────
 * `muted` — sin él ningún navegador deja que un vídeo arranque solo, y
 *   con razón: sonido no pedido en una tienda es hostil.
 * `playsInline` — sin él, Safari en iPhone abre el vídeo a pantalla
 *   completa en cuanto empieza. Se lleva la página entera por delante.
 * `loop` — es un bucle de diez segundos; sin esto se congela al final.
 * `poster` — lo que se ve mientras el vídeo llega. Sin póster, el hueco
 *   es negro y el titular flota sobre nada durante el primer segundo.
 *
 * ── No bloquea el pintado, y la página no salta ──────────────────────
 * `preload="metadata"` (el mismo valor que usa la landing) pide la
 * cabecera, no el archivo entero, así que el vídeo no compite con el CSS
 * ni con la fuente por el ancho de banda del primer pintado. Y no hay
 * salto de maquetación porque este elemento está posicionado en absoluto
 * dentro de una portada con alto mínimo propio: mida lo que mida el
 * vídeo, no empuja a nadie.
 *
 * ── Es decoración, no contenido ──────────────────────────────────────
 * `aria-hidden` y `tabIndex={-1}`: no dice nada que el titular no diga, y
 * sin `controls` un lector de pantalla anunciaría un reproductor que no
 * se puede manejar. `disablePictureInPicture` evita el botón flotante que
 * Chrome ofrece encima de cualquier vídeo grande.
 */
export function ShopHeroVideo({ motionOk }: ShopHeroVideoProps) {
  if (!motionOk) {
    return (
      <img
        src={POSTER_SRC}
        alt=""
        aria-hidden="true"
        decoding="async"
        className="h-full w-full object-cover"
      />
    );
  }

  return (
    <video
      className="h-full w-full object-cover"
      poster={POSTER_SRC}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src={VIDEO_SRC} type="video/mp4" />
    </video>
  );
}
